import React from 'react';
import { styled } from '@mui/material/styles';
import { Fab, Zoom, Tooltip } from '@mui/material';
import { WhatsApp } from '@mui/icons-material';

// Configuration
const WHATSAPP_PHONE = '+905323095062'; // WhatsApp contact number
const WHATSAPP_MESSAGE = 'Merhaba! Web sitenizden yazıyorum. Kiosk ve ön muhasebe çözümleriniz hakkında bilgi alabilir miyim?';

const WhatsAppButton = styled(Fab)(({ theme }) => ({
  position: 'fixed',
  bottom: theme.spacing(3),
  right: theme.spacing(3),
  backgroundColor: '#25D366',
  color: 'white',
  width: 60,
  height: 60,
  zIndex: 9999,
  boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    animation: 'none',
    backgroundColor: '#20B954',
    transform: 'scale(1.1)',
    boxShadow: '0 8px 30px rgba(37, 211, 102, 0.6)',
  },
  
  '&:active': {
    transform: 'scale(0.95)',
  },

  // Responsive adjustments
  [theme.breakpoints.down('sm')]: {
    bottom: theme.spacing(2),
    right: theme.spacing(2),
    width: 56,
    height: 56,
  },

  // Animation on mount
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
    },
    '50%': {
      boxShadow: '0 4px 20px rgba(37, 211, 102, 0.8)',
    },
    '100%': {
      boxShadow: '0 4px 20px rgba(37, 211, 102, 0.4)',
    },
  },

  animation: 'pulse 2s infinite',
}));

interface WhatsAppFloatingButtonProps {
  phoneNumber?: string;
  message?: string;
  show?: boolean;
}

const WhatsAppFloatingButton: React.FC<WhatsAppFloatingButtonProps> = ({ 
  phoneNumber = WHATSAPP_PHONE,
  message = WHATSAPP_MESSAGE,
  show = true 
}) => {
  const handleWhatsAppClick = () => {
    // URL encode the message for better compatibility
    const encodedMessage = encodeURIComponent(message);
    
    // Clean phone number (remove all non-digits)
    const cleanPhone = phoneNumber.replace(/[^\d]/g, '');
    
    // Detect if user is on mobile device
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    
    // Create different URLs for mobile and desktop
    let whatsappUrl: string;
    
    if (isMobile) {
      // For mobile devices, try the app deep link first
      whatsappUrl = `whatsapp://send?phone=${cleanPhone}&text=${encodedMessage}`;
      
      // Fallback to web version if app doesn't open
      setTimeout(() => {
        window.location.href = `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
      }, 2500);
      
      window.location.href = whatsappUrl;
    } else {
      // For desktop, use web version directly
      whatsappUrl = `https://web.whatsapp.com/send?phone=${cleanPhone}&text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Zoom in={show} timeout={300}>
      <Tooltip 
        title="WhatsApp ile İletişime Geç" 
        placement="left"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              bgcolor: '#333',
              color: 'white',
              fontSize: '0.875rem',
              '& .MuiTooltip-arrow': {
                color: '#333',
              },
            },
          },
        }}
      >
        <WhatsAppButton
          onClick={handleWhatsAppClick}
          aria-label="WhatsApp ile iletişime geç"
        >
          <WhatsApp sx={{ fontSize: 28 }} />
        </WhatsAppButton>
      </Tooltip>
    </Zoom>
  );
};

export default WhatsAppFloatingButton;
